import {
  type Asset,
  deleteAsset,
  listVisibleAssets,
  setAssetScope,
  subscribeAssets,
  uploadAsset,
} from './library';
import { getState, subscribe, updateProject } from '../store/project';
import type { Background } from '../types';

type Tab = 'color' | 'gradient' | 'media';

export class BackgroundModal {
  private bg!: HTMLElement;
  private body!: HTMLElement;
  private tabBar!: HTMLElement;
  private tab: Tab = 'color';

  constructor() {
    this.build();
    subscribe(() => {
      if (this.bg.classList.contains('show')) this.renderTab();
    });
    subscribeAssets(() => {
      if (this.bg.classList.contains('show') && this.tab === 'media') this.renderTab();
    });
  }

  private build(): void {
    this.bg = document.createElement('div');
    this.bg.className = 'modal-bg';
    const modal = document.createElement('div');
    modal.className = 'modal modal-lg';

    const h = document.createElement('h3');
    h.textContent = '🖼 배경';
    const desc = document.createElement('p');
    desc.textContent =
      '단색 / 그라데이션 / 이미지 / 동영상. 업로드 자산은 이 프로젝트 또는 모든 프로젝트에서 공유 가능.';

    this.tabBar = document.createElement('div');
    this.tabBar.className = 'tab-bar';
    for (const [id, label] of [
      ['color', '단색'],
      ['gradient', '그라데이션'],
      ['media', '이미지/동영상'],
    ] as Array<[Tab, string]>) {
      const b = document.createElement('button');
      b.className = 'btn ghost';
      b.textContent = label;
      b.dataset.tab = id;
      b.addEventListener('click', () => this.setTab(id));
      this.tabBar.appendChild(b);
    }

    this.body = document.createElement('div');
    this.body.style.marginTop = '12px';

    const actions = document.createElement('div');
    actions.className = 'row-actions';
    const close = document.createElement('button');
    close.className = 'btn primary';
    close.textContent = '닫기';
    close.addEventListener('click', () => this.close());
    actions.appendChild(close);

    modal.append(h, desc, this.tabBar, this.body, actions);
    this.bg.appendChild(modal);
    this.bg.addEventListener('click', (e) => {
      if (e.target === this.bg) this.close();
    });
    document.body.appendChild(this.bg);
  }

  open(): void {
    this.bg.classList.add('show');
    const cur = getState().project.canvas.background;
    if (cur.kind === 'color') this.tab = 'color';
    else if (cur.kind === 'gradient') this.tab = 'gradient';
    else this.tab = 'media';
    this.setTab(this.tab);
  }
  close(): void {
    this.bg.classList.remove('show');
  }

  private setTab(t: Tab): void {
    this.tab = t;
    this.tabBar.querySelectorAll<HTMLElement>('button').forEach((b) => {
      b.classList.toggle('active', b.dataset.tab === t);
    });
    this.renderTab();
  }

  private renderTab(): void {
    this.body.replaceChildren();
    const cur = getState().project.canvas.background;
    if (this.tab === 'color') this.renderColor(cur);
    else if (this.tab === 'gradient') this.renderGradient(cur);
    else this.renderMedia(cur);
  }

  private renderColor(cur: Background): void {
    const wrap = document.createElement('div');
    wrap.style.display = 'flex';
    wrap.style.gap = '12px';
    wrap.style.alignItems = 'center';
    const color = document.createElement('input');
    color.type = 'color';
    color.value = cur.kind === 'color' ? cur.color : '#0e0e10';
    color.style.width = '64px';
    color.style.height = '40px';
    color.addEventListener('input', () => {
      updateProject((p) => ({
        ...p,
        canvas: { ...p.canvas, background: { kind: 'color', color: color.value } },
      }));
    });
    const text = document.createElement('input');
    text.type = 'text';
    text.value = color.value;
    text.style.flex = '1';
    text.addEventListener('input', () => {
      color.value = text.value;
      updateProject((p) => ({
        ...p,
        canvas: { ...p.canvas, background: { kind: 'color', color: text.value } },
      }));
    });
    wrap.append(color, text);
    this.body.appendChild(wrap);
  }

  private renderGradient(cur: Background): void {
    const g =
      cur.kind === 'gradient'
        ? cur
        : { kind: 'gradient' as const, from: '#fff8ee', to: '#ffe1c4', angle: 180 };
    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = '90px 1fr';
    grid.style.gap = '8px';

    const fromCol = document.createElement('input');
    fromCol.type = 'color';
    fromCol.value = g.from;
    const toCol = document.createElement('input');
    toCol.type = 'color';
    toCol.value = g.to;
    const angle = document.createElement('input');
    angle.type = 'number';
    angle.value = String(g.angle);
    angle.min = '0';
    angle.max = '360';

    const apply = (): void => {
      updateProject((p) => ({
        ...p,
        canvas: {
          ...p.canvas,
          background: {
            kind: 'gradient',
            from: fromCol.value,
            to: toCol.value,
            angle: parseInt(angle.value, 10) || 0,
          },
        },
      }));
    };
    [fromCol, toCol, angle].forEach((el) => el.addEventListener('input', apply));

    grid.append(
      labeled('From', fromCol),
      labeled('To', toCol),
      labeled('각도', angle),
    );
    this.body.appendChild(grid);
  }

  private async renderMedia(cur: Background): Promise<void> {
    // Upload row — uploads always land in the current project; user can
    // promote to "all projects" via the per-tile button below.
    const uploadRow = document.createElement('div');
    uploadRow.style.display = 'flex';
    uploadRow.style.gap = '8px';
    uploadRow.style.alignItems = 'center';
    uploadRow.style.padding = '10px';
    uploadRow.style.background = 'var(--bg-elev)';
    uploadRow.style.border = '1px solid var(--line)';
    uploadRow.style.borderRadius = '8px';
    uploadRow.style.marginBottom = '12px';

    const upload = document.createElement('input');
    upload.type = 'file';
    upload.accept = 'image/*,video/*';
    upload.style.flex = '1';

    const upBtn = document.createElement('button');
    upBtn.className = 'btn primary';
    upBtn.textContent = '업로드 + 적용';
    upBtn.addEventListener('click', async () => {
      const file = upload.files?.[0];
      if (!file) {
        alert('파일을 선택하세요');
        return;
      }
      const projectId = getState().project.id;
      const asset = await uploadAsset(file, projectId, 'project');
      this.applyAsset(asset);
    });

    uploadRow.append(upload, upBtn);
    this.body.appendChild(uploadRow);

    // Two asset sections: this project / other projects
    const projectId = getState().project.id;
    const assets = (await listVisibleAssets(projectId)).filter(
      (a) => a.kind === 'image' || a.kind === 'video',
    );

    const own = assets.filter((a) => a.projectId === projectId);
    const others = assets.filter((a) => a.projectId !== projectId);

    this.body.appendChild(this.buildAssetSection('이 프로젝트의 어셋', own, cur, projectId));
    this.body.appendChild(this.buildAssetSection('다른 프로젝트의 어셋', others, cur, projectId));
  }

  private buildAssetSection(
    title: string,
    assets: Asset[],
    cur: Background,
    projectId: string,
  ): HTMLElement {
    const section = document.createElement('div');
    section.className = 'asset-section';

    const head = document.createElement('div');
    head.className = 'asset-section-head';
    head.textContent = `${title} · ${assets.length}`;
    section.appendChild(head);

    const grid = document.createElement('div');
    grid.className = 'asset-grid';
    section.appendChild(grid);

    if (assets.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'asset-empty';
      empty.textContent = '업로드한 자산이 없습니다';
      grid.appendChild(empty);
    } else {
      for (const a of assets) {
        grid.appendChild(this.buildAssetTile(a, cur, projectId));
      }
    }
    return section;
  }

  private buildAssetTile(asset: Asset, cur: Background, projectId: string): HTMLElement {
    const tile = document.createElement('div');
    tile.className = 'asset-tile';

    const isCurrent =
      (cur.kind === 'image' || cur.kind === 'video') && cur.src === asset.src;
    if (isCurrent) tile.classList.add('current');

    const preview = document.createElement('div');
    preview.className = 'asset-preview';
    if (asset.kind === 'image') {
      const img = document.createElement('img');
      img.src = asset.src;
      img.loading = 'lazy';
      preview.appendChild(img);
    } else {
      const v = document.createElement('video');
      v.src = asset.src;
      v.muted = true;
      v.playsInline = true;
      preview.appendChild(v);
    }
    preview.addEventListener('click', () => this.applyAsset(asset));

    const meta = document.createElement('div');
    meta.className = 'asset-meta';
    const name = document.createElement('div');
    name.className = 'asset-name';
    name.textContent = asset.name;
    name.title = asset.name;
    const tags = document.createElement('div');
    tags.className = 'asset-tags';
    const scopeBadge = document.createElement('span');
    scopeBadge.className = 'tag';
    scopeBadge.textContent = asset.scope === 'global' ? '🌐 전체' : '📁 이 프로젝트';
    if (asset.scope === 'project' && asset.projectId !== projectId) {
      scopeBadge.textContent = '📁 다른 프로젝트';
    }
    const sizeBadge = document.createElement('span');
    sizeBadge.className = 'tag';
    sizeBadge.textContent = `${Math.round(asset.sizeBytes / 1024)} KB`;
    tags.append(scopeBadge, sizeBadge);
    meta.append(name, tags);

    const actions = document.createElement('div');
    actions.className = 'asset-actions';

    const useBtn = document.createElement('button');
    useBtn.className = 'btn primary';
    useBtn.textContent = isCurrent ? '✓ 적용됨' : '적용';
    useBtn.disabled = isCurrent;
    useBtn.addEventListener('click', () => this.applyAsset(asset));

    const promote = document.createElement('button');
    promote.className = 'btn ghost';
    promote.textContent = asset.scope === 'global' ? '🔒 이 프로젝트로' : '🌐 전체로';
    promote.title =
      asset.scope === 'global'
        ? '다른 프로젝트에서 안 보이게 하기'
        : '다른 프로젝트에서도 사용 가능하게 하기';
    promote.addEventListener('click', async () => {
      await setAssetScope(asset.id, asset.scope === 'global' ? 'project' : 'global');
    });

    const del = document.createElement('button');
    del.className = 'btn ghost';
    del.textContent = '✕';
    del.title = '삭제';
    del.addEventListener('click', async () => {
      if (!confirm(`"${asset.name}"을 삭제할까요?`)) return;
      await deleteAsset(asset.id);
    });

    actions.append(useBtn, promote, del);

    tile.append(preview, meta, actions);
    return tile;
  }

  private applyAsset(asset: Asset): void {
    updateProject((p) => {
      const bg: Background =
        asset.kind === 'video'
          ? { kind: 'video', src: asset.src, fit: 'cover', loop: true, muted: true }
          : { kind: 'image', src: asset.src, fit: 'cover' };
      return { ...p, canvas: { ...p.canvas, background: bg } };
    });
  }
}

function labeled(label: string, control: HTMLElement): HTMLElement {
  const w = document.createElement('div');
  w.style.display = 'contents';
  const l = document.createElement('span');
  l.textContent = label;
  l.style.color = 'var(--ink-mute)';
  l.style.fontSize = '12px';
  l.style.alignSelf = 'center';
  w.append(l, control);
  return w;
}
